/**
 * TraceTreeSection — visualises the span tree from analysis.context.trace.
 *
 * The error span (if any) is highlighted in red. SQL queries are surfaced
 * as a separate sub-section because they're the highest-signal forensic
 * detail in most RCAs (the actual statement that ran, not just "DB call").
 *
 * Spans are listed in DFS order with depth-based indentation. We don't draw
 * connector lines — the indent + service/resource label is enough density
 * for an engineer-facing view.
 */

import {
  Box,
  Chip,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import type { TraceDetail, TraceSpan } from '@/types/analysis';

export interface TraceTreeSectionProps {
  trace: TraceDetail;
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)}s`;
  if (ms >= 10) return `${Math.round(ms)}ms`;
  return `${ms.toFixed(1)}ms`;
}

function formatHttpStatusColor(code: number | null): 'error' | 'warning' | 'success' | 'default' {
  if (code == null) return 'default';
  if (code >= 500) return 'error';
  if (code >= 400) return 'warning';
  if (code >= 200 && code < 300) return 'success';
  return 'default';
}

function SpanRow({ span }: { span: TraceSpan }) {
  const hasError = span.error !== null;
  return (
    <Box
      sx={{
        pl: span.depth * 2,
        py: 0.5,
        borderLeft: hasError ? '2px solid' : '2px solid',
        borderColor: hasError ? 'error.main' : 'divider',
        bgcolor: hasError ? 'rgba(239, 68, 68, 0.10)' : 'transparent',
        color: hasError ? 'error.main' : undefined,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
          {span.service ?? 'unknown'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {span.resource ?? '—'}
        </Typography>
        {span.http_status_code != null && (
          <Chip
            size="small"
            label={span.http_status_code}
            color={formatHttpStatusColor(span.http_status_code)}
            sx={{ height: 18, fontSize: 10 }}
          />
        )}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {formatDuration(span.duration_ms)}
        </Typography>
      </Stack>
      {hasError && span.error && (
        <Box sx={{ pl: 1, mt: 0.5, color: 'error.main' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>
            {span.error.type ?? 'Error'}: {span.error.message ?? ''}
          </Typography>
          {span.error.stack && (
            <Box
              component="pre"
              sx={{
                mt: 0.5,
                mb: 0,
                p: 1,
                bgcolor: 'grey.900',
                color: 'grey.100',
                fontSize: 11,
                fontFamily: 'monospace',
                borderRadius: 0.5,
                overflow: 'auto',
                maxHeight: 240,
                lineHeight: 1.5,
              }}
            >
              {span.error.stack}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export function TraceTreeSection({ trace }: TraceTreeSectionProps) {
  const root = trace.root;
  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Trace
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {root && (
          <>
            <Chip
              size="small"
              variant="outlined"
              label={`${root.service ?? 'unknown'} · ${root.resource ?? '—'}`}
              sx={{ fontFamily: 'monospace' }}
            />
            {root.http_status_code != null && (
              <Chip
                size="small"
                label={`HTTP ${root.http_status_code}`}
                color={formatHttpStatusColor(root.http_status_code)}
              />
            )}
          </>
        )}
        <Typography variant="caption" color="text.secondary">
          {trace.span_count} span{trace.span_count !== 1 ? 's' : ''} · {formatDuration(trace.duration_ms)}
          {trace.truncated_spans > 0 && ` · ${trace.truncated_spans} non-error spans dropped`}
        </Typography>
      </Stack>

      {trace.sql_queries.length > 0 && (
        <Accordion disableGutters defaultExpanded sx={{ mb: 2 }} elevation={0} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
            <Typography variant="subtitle2">
              SQL queries · {trace.sql_queries.length}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {trace.sql_queries.map((q, i) => (
                <Box key={i}>
                  <Typography variant="caption" color="text.secondary">
                    {q.service ?? 'db'} · {formatDuration(q.duration_ms)}
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      mt: 0.5,
                      p: 1,
                      bgcolor: 'background.elev2',
                      color: 'text.primary',
                      borderRadius: 0.5,
                      fontSize: 12,
                      fontFamily: 'monospace',
                      overflow: 'auto',
                      lineHeight: 1.5,
                    }}
                  >
                    {q.statement}
                  </Box>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion disableGutters defaultExpanded elevation={0} variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44 }}>
          <Typography variant="subtitle2">Spans</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Stack spacing={0} divider={<Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />}>
            {trace.spans.map((span) => (
              <SpanRow key={span.span_id} span={span} />
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
